//
// # PropertiesPanel
//
// Docked right-sidebar panel showing/editing the properties of the selected
// entity. Switches on the descriptor kind and composes section components
// top-to-bottom, then an Apply/Revert footer. `null` / `multi` / `unsupported`
// render placeholders (no footer).
//
// Draft edits live in local React state, keyed to entity identity. A useEffect
// re-seeds the draft whenever the selection changes (selectionVersion) or the
// underlying content changes externally (contentVersion) — so buffered edits
// auto-discard on deselect / select-different / external edit. Apply dispatches
// only the dirty fields through `workspace.applyEntityEdits`; Revert re-seeds
// the draft from the descriptor.
//

import React, { useCallback, useEffect, useRef, useState } from "react";
import { paramValueToString } from "CoreUtils";
import { Ident } from "GenAlphaModel";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import { usePropertiesDescriptor } from "./usePropertiesDescriptor";
import { buildDraft, draftToEdits, hasDirtyFields, type Draft } from "./draft";
import { convertParamValues, omitDefaultParams } from "../common/ParamFields";
import {
  EntityTypeSection,
  EntityNameSection,
  PositionSection,
  LayerSection,
  TransformSection,
  SchPinPropertiesSection,
  LayoutPinPropertiesSection,
  InstancePropertiesSection,
  OutlineSection,
} from "./sections";
import type { GeneratorParamSpec, SymbolInfo } from "ServerInterface";
import type { EntityDescriptor } from "../../types";
import "./propertiesPanel.css";

// Placeholder spec used while params are still loading (renders no fields).
const EMPTY_PARAM_SPEC: GeneratorParamSpec = {
  name: "",
  description: "",
  params: [],
};

// Narrow a draft rotation number to the 0/90/180/270 literal union.
function toRotationLiteral(value: number): 0 | 90 | 180 | 270 {
  switch (value) {
    case 90:
      return 90;
    case 180:
      return 180;
    case 270:
      return 270;
    default:
      return 0;
  }
}

// A stable key for the selected entity, used to detect identity changes.
function entityKeyOf(descriptor: EntityDescriptor | null): string {
  if (descriptor === null) return "none";
  if (descriptor.kind === "multi") return "multi";
  if (descriptor.kind === "unsupported")
    return `unsupported:${descriptor.entityKind}`;
  return `${Ident.stringify(descriptor.designIdent)}:${descriptor.kind}:${
    descriptor.id
  }`;
}

export function PropertiesPanel() {
  const workspace = useWorkspace();
  const descriptor = usePropertiesDescriptor();
  const entityKey = entityKeyOf(descriptor);

  const [draft, setDraft] = useState<Draft | null>(() =>
    buildDraft(descriptor)
  );
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  // Param specs for types offered by the type picker, fetched on demand (the
  // symbol list no longer carries them) and cached by symbol name. A `null`
  // value is a resolved answer — that type has no generator params — and is
  // distinct from `undefined`, meaning "not fetched yet".
  const [typeParamSpecs, setTypeParamSpecs] = useState<
    ReadonlyMap<string, GeneratorParamSpec | null>
  >(new Map());
  // Name of the type whose spec fetch is currently in flight, so a superseded
  // fetch can't re-seed the draft when it lands late. Mirrors the
  // `pendingSymbolRef` guard in EditInstanceDialog.
  const pendingTypeRef = useRef<string | null>(null);
  // Same-class type candidates for the type picker (schematic instances only).
  const [symbols, setSymbols] = useState<ReadonlyArray<SymbolInfo>>([]);

  // Fetch the type-change candidates when a schematic instance is selected.
  const isSchInstance = descriptor !== null && descriptor.kind === "instance";
  const designIdentStr =
    descriptor !== null &&
    descriptor.kind !== "multi" &&
    descriptor.kind !== "unsupported"
      ? Ident.stringify(descriptor.designIdent)
      : null;
  useEffect(() => {
    if (!isSchInstance || designIdentStr === null) {
      setSymbols([]);
      return;
    }
    let cancelled = false;
    const live = workspace.properties.describeSelection();
    if (live === null || live.kind !== "instance") return;
    void workspace.properties
      .getAvailableSymbolsForDesign(live.designIdent)
      .then((list) => {
        if (!cancelled) setSymbols(list);
      });
    return () => {
      cancelled = true;
    };
  }, [isSchInstance, designIdentStr, workspace]);

  // Re-seed the draft when the selected entity changes or the underlying
  // content changes beneath the panel (discarding unsaved edits — matching the
  // documented auto-discard-on-external-change behavior).
  useEffect(() => {
    setDraft(buildDraft(descriptor));
    setApplyError(null);
    // An in-flight fetch belongs to the entity we just left.
    pendingTypeRef.current = null;
    // entityKey changes on select/deselect/select-different; the descriptor
    // identity changes on every re-derive (selectionVersion / contentVersion).
  }, [entityKey, descriptor]);

  const revert = useCallback(() => {
    setDraft(buildDraft(descriptor));
    setApplyError(null);
  }, [descriptor]);

  // Per-field draft update handlers (block bodies — they return void). Using
  // functional setState avoids stale-closure issues on the current draft.
  const onName = useCallback((name: string) => {
    setDraft((d) => (d === null ? d : { ...d, name }));
  }, []);
  const onPosition = useCallback((position: Draft["position"]) => {
    setDraft((d) => (d === null ? d : { ...d, position }));
  }, []);
  const onRotation = useCallback((rotation: number) => {
    setDraft((d) => (d === null ? d : { ...d, rotation }));
  }, []);
  const onReflected = useCallback((reflected: boolean) => {
    setDraft((d) => (d === null ? d : { ...d, reflected }));
  }, []);
  const onDirection = useCallback((direction: Draft["direction"]) => {
    setDraft((d) => (d === null ? d : { ...d, direction }));
  }, []);
  const onLayer = useCallback((layer: number) => {
    setDraft((d) => (d === null ? d : { ...d, layer }));
  }, []);
  const onPinType = useCallback((pinType: Draft["pinType"]) => {
    setDraft((d) => (d === null ? d : { ...d, pinType }));
  }, []);
  const onShowOutlines = useCallback(
    (show: boolean) => {
      workspace.properties.setShowOutlines(show);
    },
    [workspace]
  );

  // Type picker: when a new type is chosen, record it and re-seed the param
  // draft from the new type's spec, carrying over old values by matching name.
  //
  // The spec is fetched per type rather than read off the symbol list, which
  // stopped carrying specs once external cells started declaring their real
  // CDF params — see `get-symbol-param-spec` in ServerInterface. Cached by
  // name so flipping back and forth between two types doesn't re-request.
  const onType = useCallback(
    (typeName: string) => {
      const seed = (spec: GeneratorParamSpec | null) => {
        setDraft((d) => {
          if (d === null) return d;
          const oldParams = d.params ?? {};
          const newParams: Record<string, string> = {};
          for (const p of spec?.params ?? []) {
            // Carry over the old value when the param name matches, else the
            // spec default (stringified), else empty.
            if (Object.prototype.hasOwnProperty.call(oldParams, p.name)) {
              newParams[p.name] = oldParams[p.name];
            } else if (p.default !== undefined) {
              newParams[p.name] = paramValueToString(p.default).unwrapOr("");
            } else {
              newParams[p.name] = "";
            }
          }
          return { ...d, newTypeName: typeName, params: newParams };
        });
      };

      // Claimed on every pick, cached too: an earlier in-flight fetch would
      // otherwise still match the ref and seed on top of this one.
      pendingTypeRef.current = typeName;
      if (typeParamSpecs.has(typeName)) {
        seed(typeParamSpecs.get(typeName) ?? null);
        return;
      }
      void (async () => {
        const sym = symbols.find((s) => s.name === typeName);
        const result = await workspace.getSymbolParamSpec(typeName, sym?.pdkId);
        const spec = result.err ? null : result.val;
        // Cached even if superseded — keyed by type name, never stale.
        setTypeParamSpecs((prev) => new Map(prev).set(typeName, spec));
        // Seeding is different: two quick picks can land out of order, and
        // the slower reply would set `newTypeName` back to the old type.
        if (pendingTypeRef.current !== typeName) return;
        seed(spec);
      })();
    },
    [symbols, workspace, typeParamSpecs]
  );

  // Param-form setter adapter: ParamFields expects a
  // React.Dispatch<SetStateAction<Record<string,string>>>; route updates into
  // the draft's `params` slice.
  const setParamValues = useCallback<
    React.Dispatch<React.SetStateAction<Record<string, string>>>
  >((action) => {
    setDraft((d) => {
      if (d === null) return d;
      const prev = d.params ?? {};
      const next = typeof action === "function" ? action(prev) : action;
      return { ...d, params: next };
    });
  }, []);

  const apply = useCallback(async () => {
    if (descriptor === null || draft === null) return;
    if (descriptor.kind === "multi" || descriptor.kind === "unsupported") {
      return;
    }

    // Type change (schematic instance): delete + create. Applied as its own
    // operation (not part of the field-edit Combo). The new type's param spec
    // determines validation; carry the (already-reseeded) param draft.
    if (
      descriptor.kind === "instance" &&
      draft.newTypeName !== undefined &&
      draft.newTypeName !== descriptor.typeName
    ) {
      // Fetched by `onType` when the type was picked, so it's in the cache by
      // the time Apply is reachable.
      const newTypeSpec = typeParamSpecs.get(draft.newTypeName);
      let typedParams: Record<string, unknown> | undefined;
      if (newTypeSpec) {
        const converted = convertParamValues(newTypeSpec, draft.params ?? {});
        if (!converted.ok) {
          setApplyError(converted.error);
          return;
        }
        // Retype is delete + create — no stored params to overwrite.
        typedParams = omitDefaultParams(newTypeSpec, converted.val);
      }
      setApplying(true);
      setApplyError(null);
      try {
        const result = await workspace.properties.changeInstanceType({
          designIdent: descriptor.designIdent,
          oldInstanceId: descriptor.id,
          newSymbolName: draft.newTypeName,
          name: draft.name,
          position: draft.position,
          rotation: toRotationLiteral(draft.rotation),
          reflected: draft.reflected,
          params: typedParams,
        });
        if (result.err) setApplyError(result.val.why);
      } finally {
        setApplying(false);
      }
      return;
    }

    const edits = draftToEdits(descriptor, draft);
    if (edits === null) return;

    // If params changed, validate + coerce raw input strings to typed values
    // against the spec before dispatching.
    let typedEdits = edits;
    if (edits.params !== undefined) {
      const isInstance =
        descriptor.kind === "instance" || descriptor.kind === "layoutInstance";
      const spec = isInstance ? descriptor.paramSpec : undefined;
      const stored = isInstance ? descriptor.params : undefined;
      if (spec !== undefined) {
        const converted = convertParamValues(spec, draft.params ?? {});
        if (!converted.ok) {
          setApplyError(converted.error);
          return;
        }
        // `stored` so a field reset to its default still overwrites.
        typedEdits = {
          ...edits,
          params: omitDefaultParams(spec, converted.val, stored),
        };
      }
    }

    setApplying(true);
    setApplyError(null);
    try {
      const result = await workspace.properties.applyEntityEdits(typedEdits);
      if (result.err) {
        setApplyError(result.val.why);
      }
    } finally {
      setApplying(false);
    }
    // `typeParamSpecs`, not `symbols`: the pending type's spec is read from
    // that cache above. A stale map leaves `typedParams` undefined and the
    // retyped instance at its generator's defaults.
  }, [workspace, descriptor, draft, typeParamSpecs]);

  if (descriptor === null) {
    return (
      <div className="properties-panel">
        <div className="properties-panel-empty">
          No selection. Select an entity to view its properties.
        </div>
        <ActiveDesignInfoSection />
      </div>
    );
  }

  if (descriptor.kind === "multi") {
    return (
      <div className="properties-panel">
        <div className="properties-panel-empty">
          {descriptor.count} entities selected.
        </div>
      </div>
    );
  }

  if (descriptor.kind === "unsupported") {
    return (
      <div className="properties-panel">
        <div className="properties-panel-empty">
          {descriptor.entityKind}: No editable properties for this entity type.
        </div>
      </div>
    );
  }

  if (draft === null) {
    return (
      <div className="properties-panel">
        <div className="properties-panel-empty">Loading…</div>
      </div>
    );
  }

  const dirty = hasDirtyFields(descriptor, draft);

  // Per-field handlers bundled so each per-kind section renderer takes one
  // compact prop instead of a long handler list.
  const handlers: FieldHandlers = {
    applying,
    onName,
    onPosition,
    onRotation,
    onReflected,
    onDirection,
    onLayer,
    onPinType,
    onShowOutlines,
    setParamValues,
  };

  return (
    <div className="properties-panel">
      <div className="properties-panel-sections">
        {descriptor.kind === "instance" && (
          <InstanceSections
            descriptor={descriptor}
            draft={draft}
            symbols={symbols}
            typeParamSpecs={typeParamSpecs}
            onType={onType}
            handlers={handlers}
          />
        )}
        {descriptor.kind === "schPin" && (
          <SchPinSections draft={draft} handlers={handlers} />
        )}
        {descriptor.kind === "layoutInstance" && (
          <LayoutInstanceSections
            descriptor={descriptor}
            draft={draft}
            handlers={handlers}
          />
        )}
        {descriptor.kind === "layoutPin" && (
          <LayoutPinSections draft={draft} handlers={handlers} />
        )}
      </div>

      <div className="properties-footer">
        {applyError !== null && (
          <div className="properties-error">Apply failed: {applyError}</div>
        )}
        <div className="properties-footer-buttons">
          <button
            type="button"
            className="properties-btn"
            disabled={!dirty || applying}
            onClick={revert}
          >
            Revert
          </button>
          <button
            type="button"
            className="properties-btn properties-btn-primary"
            disabled={!dirty || applying}
            onClick={() => void apply()}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

// Capitalize a contentKind ("schematic" -> "Schematic") for display. Returns a
// dash for empty input rather than an empty string.
function formatContentKind(kind: string): string {
  if (kind === "") return "—";
  return kind.charAt(0).toUpperCase() + kind.slice(1);
}

// Bottom-of-panel summary of the active design, shown when nothing is selected
// so the (otherwise empty) panel still surfaces useful context. Renders nothing
// when no design is open.
function ActiveDesignInfoSection() {
  const { activeIdent, activeDesignInfo, tabs } = useWorkspaceSnapshot();
  if (activeIdent === null || activeDesignInfo === null) return null;

  const activeIdentStr = Ident.stringify(activeIdent);
  const displayName =
    tabs.find((t) => Ident.stringify(t.ident) === activeIdentStr)
      ?.displayName ?? null;

  return (
    <div className="properties-design-info">
      <div className="properties-design-info-title">Design</div>
      {displayName !== null && (
        <div className="properties-design-info-row">
          <span className="properties-design-info-label">Name</span>
          <span className="properties-design-info-value" title={displayName}>
            {displayName}
          </span>
        </div>
      )}
      <div className="properties-design-info-row">
        <span className="properties-design-info-label">Type</span>
        <span className="properties-design-info-value">
          {formatContentKind(activeDesignInfo.contentKind)}
        </span>
      </div>
      <div className="properties-design-info-row">
        <span className="properties-design-info-label">PDK</span>
        <span
          className="properties-design-info-value"
          title={activeDesignInfo.pdkId ?? undefined}
        >
          {activeDesignInfo.pdkId ?? "—"}
        </span>
      </div>
    </div>
  );
}

// Per-field draft handlers shared by all section renderers below. Bundled to
// keep the renderer signatures (and the call sites above) compact.
interface FieldHandlers {
  readonly applying: boolean;
  readonly onName: (name: string) => void;
  readonly onPosition: (position: Draft["position"]) => void;
  readonly onRotation: (rotation: number) => void;
  readonly onReflected: (reflected: boolean) => void;
  readonly onDirection: (direction: Draft["direction"]) => void;
  readonly onLayer: (layer: number) => void;
  readonly onPinType: (pinType: Draft["pinType"]) => void;
  readonly onShowOutlines: (show: boolean) => void;
  readonly setParamValues: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
}

// Schematic instance: type picker, name, transform, generator params. When a
// type change is pending, render the NEW type's param spec (so the user edits
// the new generator's params); otherwise the instance's own spec.
function InstanceSections(props: {
  readonly descriptor: Extract<EntityDescriptor, { kind: "instance" }>;
  readonly draft: Draft;
  readonly symbols: ReadonlyArray<SymbolInfo>;
  // Specs for picker types, fetched on demand by `onType`. `undefined` for a
  // name means its fetch hasn't landed yet.
  readonly typeParamSpecs: ReadonlyMap<string, GeneratorParamSpec | null>;
  readonly onType: (typeName: string) => void;
  readonly handlers: FieldHandlers;
}) {
  const { descriptor, draft, symbols, typeParamSpecs, onType, handlers } =
    props;
  const { applying } = handlers;

  // A pending type change shows the NEW type's spec. Its fetch may still be in
  // flight, in which case show the loading state rather than falling back to
  // the instance's own spec — that would put the OLD type's fields under the
  // new type's name.
  const pendingTypeName =
    draft.newTypeName !== undefined && draft.newTypeName !== descriptor.typeName
      ? draft.newTypeName
      : undefined;
  const pendingSpec =
    pendingTypeName !== undefined
      ? typeParamSpecs.get(pendingTypeName)
      : undefined;
  const paramSpec =
    pendingTypeName !== undefined ? pendingSpec ?? null : descriptor.paramSpec;
  const paramsLoading =
    pendingTypeName !== undefined
      ? pendingSpec === undefined
      : descriptor.paramsLoading;

  return (
    <>
      <EntityTypeSection
        value={draft.newTypeName ?? descriptor.typeName}
        candidates={symbols.map((s) => s.name)}
        onChange={onType}
        disabled={applying}
      />
      <EntityNameSection
        value={draft.name}
        onChange={handlers.onName}
        disabled={applying}
      />
      <PositionSection
        position={draft.position}
        onChange={handlers.onPosition}
        disabled={applying}
      />
      <TransformSection
        rotation={draft.rotation}
        reflected={draft.reflected}
        onRotationChange={handlers.onRotation}
        onReflectedChange={handlers.onReflected}
        disabled={applying}
      />
      {(paramSpec !== undefined || paramsLoading) && (
        <InstancePropertiesSection
          paramSpec={paramSpec ?? EMPTY_PARAM_SPEC}
          paramValues={draft.params ?? {}}
          setParamValues={handlers.setParamValues}
          loading={paramsLoading}
          disabled={applying}
        />
      )}
    </>
  );
}

// Schematic pin: name, transform, direction.
function SchPinSections(props: {
  readonly draft: Draft;
  readonly handlers: FieldHandlers;
}) {
  const { draft, handlers } = props;
  const { applying } = handlers;
  return (
    <>
      <EntityNameSection
        value={draft.name}
        onChange={handlers.onName}
        disabled={applying}
      />
      <PositionSection
        position={draft.position}
        onChange={handlers.onPosition}
        disabled={applying}
      />
      <TransformSection
        rotation={draft.rotation}
        reflected={draft.reflected}
        onRotationChange={handlers.onRotation}
        onReflectedChange={handlers.onReflected}
        disabled={applying}
      />
      {draft.direction !== undefined && (
        <SchPinPropertiesSection
          direction={draft.direction}
          onChange={handlers.onDirection}
          disabled={applying}
        />
      )}
    </>
  );
}

// Layout instance: name, transform, generator params, cell outline.
function LayoutInstanceSections(props: {
  readonly descriptor: Extract<EntityDescriptor, { kind: "layoutInstance" }>;
  readonly draft: Draft;
  readonly handlers: FieldHandlers;
}) {
  const { descriptor, draft, handlers } = props;
  const { applying } = handlers;
  return (
    <>
      <EntityNameSection
        value={draft.name}
        onChange={handlers.onName}
        disabled={applying}
      />
      <PositionSection
        position={draft.position}
        onChange={handlers.onPosition}
        disabled={applying}
      />
      <TransformSection
        rotation={draft.rotation}
        reflected={draft.reflected}
        onRotationChange={handlers.onRotation}
        onReflectedChange={handlers.onReflected}
        disabled={applying}
      />
      {(descriptor.paramSpec !== undefined || descriptor.paramsLoading) && (
        <InstancePropertiesSection
          paramSpec={descriptor.paramSpec ?? EMPTY_PARAM_SPEC}
          paramValues={draft.params ?? {}}
          setParamValues={handlers.setParamValues}
          loading={descriptor.paramsLoading}
          disabled={applying}
        />
      )}
      <OutlineSection
        extents={descriptor.outlineExtents}
        showOutlines={descriptor.showOutlines}
        onShowOutlinesChange={handlers.onShowOutlines}
        disabled={applying}
      />
    </>
  );
}

// Layout pin: name, position, layer, pin type.
function LayoutPinSections(props: {
  readonly draft: Draft;
  readonly handlers: FieldHandlers;
}) {
  const { draft, handlers } = props;
  const { applying } = handlers;
  return (
    <>
      <EntityNameSection
        value={draft.name}
        onChange={handlers.onName}
        disabled={applying}
      />
      <PositionSection
        position={draft.position}
        onChange={handlers.onPosition}
        disabled={applying}
      />
      {draft.layer !== undefined && (
        <LayerSection
          layer={draft.layer}
          onChange={handlers.onLayer}
          disabled={applying}
        />
      )}
      <LayoutPinPropertiesSection
        pinType={draft.pinType}
        onChange={handlers.onPinType}
        disabled={applying}
      />
    </>
  );
}
